'use client';

import { ReactNode } from 'react';
import Image from 'next/image';

import { formatDistanceToNow } from 'date-fns';

import {
  Button,
  CopyBtn,
  EmptyState,
  Skeleton,
  StatusIcon,
} from '@mokoboko/components';
import {
  AtSign,
  Calendar,
  MapPin,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

import { DirectionsLink } from '@/components/directions-link';
import { Illustration } from '@/components/illustration';
import type { IllustrationName } from '@/lib/illustrations';
import type { ReceiptGuest } from '@/lib/types';
import {
  getFullName,
  getReceiptDateParts,
  getStartProximityLabel,
  mergeClassNames,
} from '@/lib/utils';
import { useTimeFormat } from '@/state/tenant';

// 'payment' is the payer-not-attendee view: a paid-for booking with no seat
// for the viewer, so it must not read as an attendance confirmation.
export type ReceiptStatus =
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'rescheduled'
  | 'payment'
  | 'waitlisted';

type ReceiptIcon = { image: string } | { illustration: IllustrationName };

// Most statuses mean the same at every studio, so their icon is a fixed file.
// Rescheduled and Payment take the studio's colour instead.
const STATUS_DISPLAY: Record<
  ReceiptStatus,
  {
    icon: ReceiptIcon;
    textColor: string;
    label: string;
    strikeTitle: boolean;
  }
> = {
  cancelled: {
    icon: { image: '/images/status-cancelled-3d.webp' },
    textColor: 'text-error-600',
    label: 'Cancelled',
    strikeTitle: true,
  },
  completed: {
    icon: { image: '/images/status-completed-3d.webp' },
    textColor: 'text-gray-700',
    label: 'Completed',
    strikeTitle: false,
  },
  rescheduled: {
    icon: { illustration: 'refresh-3d' },
    textColor: 'text-gray-700',
    label: 'Rescheduled',
    strikeTitle: false,
  },
  payment: {
    // A card rather than a currency glyph: studios bill in their own currency,
    // so a euro sign is wrong for every one of them that does not.
    icon: { illustration: 'credit-card-3d' },
    textColor: 'text-gray-700',
    label: 'Payment',
    strikeTitle: false,
  },
  confirmed: {
    icon: { image: '/images/check-3d.webp' },
    textColor: 'text-green-600',
    label: 'Confirmed',
    strikeTitle: false,
  },
  waitlisted: {
    icon: { image: '/images/status-waitlisted-3d.webp' },
    textColor: 'text-yellow-600',
    label: 'Waitlisted',
    strikeTitle: false,
  },
};

const NOTICE_ICONS = {
  // Gray, never the studio's colour: nothing was paid for.
  unpaid: { image: '/images/status-notice-3d.webp' },
  unavailable: { illustration: 'calendar-x-3d' },
  locked: { illustration: 'lock-3d' },
  confirming: { illustration: 'waitlist-hourglass-3d' },
  unreachable: { illustration: 'plug-3d' },
  waitlist: { image: '/images/status-waitlisted-3d.webp' },
  invalid: { image: '/images/status-completed-3d.webp' },
} satisfies Record<string, ReceiptIcon>;

const RECEIPT_ICON_PROPS = {
  width: 80,
  height: 80,
  className: 'h-20 w-20 object-contain',
  // Small WebPs, most of them 4x; the optimizer would cap them at 256.
  unoptimized: true,
};

const ReceiptIllustration = ({ icon }: { icon: ReceiptIcon }) =>
  'image' in icon ? (
    <Image {...RECEIPT_ICON_PROPS} src={icon.image} alt="" />
  ) : (
    <Illustration {...RECEIPT_ICON_PROPS} name={icon.illustration} alt="" />
  );

// A page with no booking of its own to show, in the empty state's layout.
export const ReceiptNotice = ({
  icon,
  title,
  description,
  role,
  children,
}: {
  icon: keyof typeof NOTICE_ICONS;
  title: string;
  description?: ReactNode;
  role?: 'alert';
  children?: ReactNode;
}) => (
  <div role={role}>
    <EmptyState
      image={<ReceiptIllustration icon={NOTICE_ICONS[icon]} />}
      title={title}
      titleAs="h2"
      description={
        description && <span className="block text-balance">{description}</span>
      }
      size="md"
      className="whitespace-pre-line"
    />
    {children}
  </div>
);

export const ReceiptStatusHeader = ({
  status,
  title,
  children,
  artwork,
  label,
  hideLabel,
  statusText,
}: {
  status: ReceiptStatus;
  title: string;
  children?: ReactNode;
  artwork?: ReactNode;
  label?: string;
  hideLabel?: boolean;
  statusText?: string;
}) => {
  const display = STATUS_DISPLAY[status];
  return (
    <div className="flex flex-col items-center text-center">
      {artwork ?? <ReceiptIllustration icon={display.icon} />}

      {/* A custom label names the kind of booking rather than its status, so
          it drops the status colour and weight that would imply one. */}
      {!hideLabel && (
        <p
          className={mergeClassNames(
            'mt-3 text-sm',
            label
              ? 'font-normal text-gray-700'
              : mergeClassNames('font-semibold', display.textColor)
          )}
        >
          {label ?? statusText ?? display.label}
        </p>
      )}

      <h2
        className={mergeClassNames(
          'heading-page',
          hideLabel ? 'mt-6' : 'mt-1.5',
          display.strikeTitle && 'line-through'
        )}
      >
        {title}
      </h2>
      {children}
    </div>
  );
};

// The text centres on the icon until it outgrows it, at three lines, and the
// icon then stays at the top.
export const ReceiptRow = ({
  icon,
  strikethrough,
  title,
  children,
}: {
  icon: LucideIcon;
  strikethrough?: boolean;
  title: ReactNode;
  children?: ReactNode;
}) => (
  <div className="flex items-center gap-4">
    <StatusIcon variant="filled" size="xl" icon={icon} className="self-start" />
    <div>
      <p
        className={mergeClassNames(
          'text-sm font-semibold text-gray-900',
          strikethrough && 'line-through'
        )}
      >
        {title}
      </p>
      {children}
    </div>
  </div>
);

export const ReceiptRowSkeleton = () => (
  <div className="flex items-center gap-4">
    <Skeleton isCircle height={48} />
    <div>
      <Skeleton text="body-sm" width={176} />
      <div className="mt-1">
        <Skeleton text="body-sm" width={112} />
      </div>
    </div>
  </div>
);

export const ReceiptDateRow = ({
  startDate,
  endDate,
  cancelled,
  completed,
}: {
  startDate: Date;
  endDate: Date;
  cancelled: boolean;
  completed: boolean;
}) => {
  const timeFormat = useTimeFormat();
  const { dateFull, dateShort, time, duration } = getReceiptDateParts(
    startDate,
    endDate,
    timeFormat
  );
  const relativeTimeInfo = getStartProximityLabel(startDate, endDate, {
    includeDays: true,
  });
  return (
    <ReceiptRow
      icon={Calendar}
      strikethrough={cancelled}
      title={
        <>
          <span className="hidden text-gray-900 sm:inline">{dateFull}</span>
          <span className="text-gray-900 sm:hidden">{dateShort}</span>
          <span className="mx-1.5 text-gray-500">·</span>
          <span className="text-gray-900">{time}</span>
          <span className="ml-1 font-normal text-gray-600">{duration}</span>
        </>
      }
    >
      {completed ? (
        <p className="mt-1 text-sm text-gray-600">
          {formatDistanceToNow(startDate, { addSuffix: true })}
        </p>
      ) : (
        relativeTimeInfo &&
        !cancelled && (
          <p
            className={mergeClassNames(
              'mt-1 text-sm',
              relativeTimeInfo.color === 'orange'
                ? 'text-orange-600'
                : 'text-green-600'
            )}
          >
            {relativeTimeInfo.label}
          </p>
        )
      )}
    </ReceiptRow>
  );
};

export const ReceiptLocationRow = ({
  location,
  directionsUrl,
  cancelled,
}: {
  location: string;
  directionsUrl: string | null;
  cancelled: boolean;
}) => (
  <ReceiptRow icon={MapPin} strikethrough={cancelled} title={location}>
    {directionsUrl && !cancelled && (
      <DirectionsLink href={directionsUrl} className="mt-1" />
    )}
  </ReceiptRow>
);

export const ReceiptEmailRow = ({ email }: { email: string }) => (
  <div className="group/copy flex items-center gap-4">
    <StatusIcon variant="filled" size="xl" icon={AtSign} />
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-gray-900">{email}</p>
      <p className="mt-1 text-sm text-gray-600">Linked email</p>
    </div>
    <CopyBtn
      text={email}
      variant="tertiary-gray"
      size="sm"
      className="ml-auto shrink-0 opacity-100 transition-opacity sm:opacity-0 sm:group-hover/copy:opacity-100"
    />
  </div>
);

// A guest the host brought, under the host's own spot. onRemove is set only
// while the host may still take the guest out.
export const ReceiptGuestRow = ({
  guest,
  subline,
  onRemove,
}: {
  guest: ReceiptGuest;
  subline: string;
  onRemove?: () => void;
}) => (
  <div className="flex items-center gap-4">
    <StatusIcon
      variant="filled"
      size="xl"
      icon={UserPlus}
      className="self-start"
    />
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-gray-900">
        {getFullName(guest.first_name, guest.last_name)}
      </p>
      <p className="mt-1 text-sm text-gray-600">{subline}</p>
    </div>
    {onRemove && (
      <div className="ml-auto shrink-0">
        <Button variant="secondary-gray" size="sm" onClick={onRemove}>
          Remove
        </Button>
      </div>
    )}
  </div>
);
